import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    TextField, Typography,
    Stack, Divider, Table, TableHead, TableBody, TableRow, TableCell,
    FormControl, Select, MenuItem
} from '@mui/material';
import * as api from './invoiceTypesApi';
import type { InvoiceType, Language, PrintDocumentTemplate } from './invoiceTypesApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell, mergeLocalizations } from '../common/detailView';

interface Props {
    invoiceTypeId: number | null;
}

export function InvoiceTypeView({ invoiceTypeId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<InvoiceType>();
    const [languages, setLanguages] = useState<Language[]>([]);
    const [templates, setTemplates] = useState<PrintDocumentTemplate[]>([]);

    const buildDefaultLoc = (lang: Language) => ({ language: lang });

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const [langs, tpls] = await Promise.all([api.getLanguages(), api.getPrintDocumentTemplates()]);
            setLanguages(langs);
            setTemplates(tpls);
            if (invoiceTypeId === null) {
                detail.setItem({
                    id: 0, name: '', printDocumentTemplate: null,
                    localizations: mergeLocalizations([], langs, buildDefaultLoc)
                });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getInvoiceType(invoiceTypeId);
                data.localizations = mergeLocalizations(data.localizations ?? [], langs, buildDefaultLoc);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [invoiceTypeId]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        await detail.withSaving(async () => {
            const item = detail.item!;
            const response = detail.isNew ? await api.createInvoiceType(item) : await api.updateInvoiceType(item);
            response.localizations = mergeLocalizations(response.localizations ?? [], languages, buildDefaultLoc);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    const updateLocalization = (languageId: number, name: string) => {
        if (!detail.item) return;
        detail.update('localizations', detail.item.localizations.map(loc =>
            loc.language.id === languageId ? { ...loc, name } : loc
        ));
    };

    return (
        <DetailViewShell<InvoiceType>
            title={detail.item?.name ?? ''}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(item) => {
                const sortedLocs = [...item.localizations].sort((a, b) =>
                    (a.language.name ?? '').localeCompare(b.language.name ?? '')
                );
                return (
                    <Stack spacing={2}>
                        <FormRow label={t('invoicetype.name')}>
                            {!detail.isEditing ? (
                                <Typography sx={sx13}>{item.name}</Typography>
                            ) : (
                                <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                    value={item.name ?? ''}
                                    onChange={e => detail.update('name', e.target.value)} />
                            )}
                        </FormRow>

                        <FormRow label={t('invoicetype.printDocumentTemplate')}>
                            {!detail.isEditing ? (
                                <Typography sx={sx13}>{item.printDocumentTemplate?.description}</Typography>
                            ) : (
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={item.printDocumentTemplate?.id ?? ''}
                                        onChange={e => {
                                            const tpl = templates.find(x => x.id === e.target.value);
                                            detail.update('printDocumentTemplate', tpl ?? null);
                                        }}
                                    >
                                        {[...templates]
                                            .sort((a, b) => a.id - b.id)
                                            .map(tpl => (
                                                <MenuItem key={tpl.id} value={tpl.id}>{tpl.description}</MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            )}
                        </FormRow>

                        <Divider />

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...sx13, fontWeight: 'bold' }}>{t('common.language')}</TableCell>
                                    <TableCell sx={{ ...sx13, fontWeight: 'bold' }}>{t('invoicetype.name')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedLocs.map(loc => (
                                    <TableRow key={loc.language.id}>
                                        <TableCell sx={{ ...sx13, width: 150 }}>{loc.language.name}</TableCell>
                                        <TableCell>
                                            {!detail.isEditing ? (
                                                <Typography sx={sx13}>{loc.name}</Typography>
                                            ) : (
                                                <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                                    value={loc.name ?? ''}
                                                    onChange={e => updateLocalization(loc.language.id, e.target.value)} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Stack>
                );
            }}
        </DetailViewShell>
    );
}
